import { getAccessToken, getIdToken, getStoredTokensAsync, redirectToLogin } from "./authService"
import { getCached, setCache, invalidateCache } from "./apiCache"

/**
 * API origin. Required in production builds — falling back to a hardcoded beta
 * URL would silently point a misconfigured prod deployment at beta data, so we
 * fail fast there. In dev we keep a convenience default for `npm run dev`.
 */
const API_BASE = (() => {
  const configured = import.meta.env.VITE_API_BASE
  if (configured) return configured
  if (import.meta.env.PROD) {
    throw new Error("VITE_API_BASE is not set. Refusing to start a production build without an explicit API origin.")
  }
  return "https://api.eclipse.beta.freshfoodproduction.wwgs.amazon.dev"
})()

/**
 * Error thrown for non-2xx API responses. Carries the HTTP {@link status} and
 * raw {@link body} so callers can branch on status (e.g. treat a 404 as
 * "not found" rather than a hard failure). {@code message} is the
 * already-user-friendly copy from {@code parseApiError}, so existing callers
 * that only read {@code error.message} keep working unchanged.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: string

  constructor(status: number, message: string, body: string) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.body = body
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
  /** Skip cache for this request (force fresh fetch). */
  skipCache?: boolean
  /** @internal Guards the single silent token-refresh retry on a 401. */
  _authRetried?: boolean
}

/**
 * API client that attaches Cognito JWT access token to every request.
 * If the token is missing or expired, redirects to login unless
 * VITE_SKIP_AUTH is set at build time (POC/demo mode).
 *
 * GET requests are cached client-side with TTL-based expiry.
 * Write requests (POST/PUT/DELETE) invalidate related cache entries
 * only after a successful response to avoid stale-cache on partial failure.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {}, skipCache = false } = options
  const fullUrl = `${API_BASE}${path}`

  // Check cache for GET requests
  if (method === "GET" && !skipCache) {
    const cached = getCached<T>(fullUrl)
    if (cached !== null) {
      return cached
    }
  }

  const skipAuth = import.meta.env.VITE_SKIP_AUTH === "true"
  // API Gateway Cognito User Pool authorizer validates ID tokens
  const token = getIdToken() || getAccessToken()
  if (!skipAuth && !token) {
    void redirectToLogin()
    throw new Error("Not authenticated — redirecting to login")
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 15000)

  try {
    const response = await fetch(fullUrl, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })

    if (!skipAuth && response.status === 401) {
      // Prevent infinite retry — only attempt refresh once
      if (options._authRetried) {
        void redirectToLogin()
        throw new Error("Session expired — redirecting to login")
      }
      // Try silent token refresh before redirecting
      const refreshed = await getStoredTokensAsync()
      if (refreshed) {
        clearTimeout(timer)
        return apiRequest<T>(path, {
          ...options,
          _authRetried: true,
          // API Gateway's Cognito authorizer validates ID tokens (see line above),
          // so the retry must carry the refreshed ID token — not the access token.
          headers: { ...headers, Authorization: `Bearer ${refreshed.idToken}` },
        })
      }
      void redirectToLogin()
      throw new Error("Session expired — redirecting to login")
    }

    // In skip-auth mode, treat 401 as a regular API error (don't redirect)
    if (skipAuth && response.status === 401) {
      console.warn("[Eclipse Dev] API returned 401 in skip-auth mode. The API Gateway requires a valid Midway token. Set VITE_SKIP_AUTH=false and sign in via Cognito.")
      throw new Error("Session expired. Please sign in again.")
    }

    if (!response.ok) {
      const errorBody = await response.text()
      throw new ApiError(response.status, parseApiError(response.status, errorBody), errorBody)
    }

    // Invalidate related cache only after confirming the write succeeded.
    // Pass the clean path prefix (e.g. "/po") — apiCache escapes it for URL
    // matching internally, avoiding the RegExp.source round-trip.
    if (method !== "GET") {
      const basePath = path.split("?")[0]
      const segments = basePath.split("/").filter(Boolean)
      if (segments.length > 0) {
        invalidateCache(`/${segments[0]}`)
      }
    }

    const data = await response.json() as T

    // Cache successful GET responses
    if (method === "GET") {
      setCache(fullUrl, data)
    }

    return data
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("Request timed out after 15 seconds")
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Parse API error responses into user-friendly messages.
 *
 * The backend returns errors as: {"__type": "ExceptionName", "message": "..."}
 * We extract the message and map status codes to appropriate UX copy.
 */
function parseApiError(status: number, body: string): string {
  // Try to extract the message from the JSON error body
  let serverMessage: string | null = null
  try {
    const parsed: unknown = JSON.parse(body)
    if (
      parsed && typeof parsed === "object" && "message" in parsed &&
      typeof (parsed as { message: unknown }).message === "string" &&
      (parsed as { message: string }).message.length > 0
    ) {
      serverMessage = (parsed as { message: string }).message
    }
  } catch {
    // Not JSON — use raw body if short enough
    if (body.length > 0 && body.length < 200) {
      serverMessage = body
    }
  }

  switch (status) {
    case 400:
      // Smithy validation errors come as "N validation errors detected: ..."
      // Rewrite them to be user-friendly
      if (serverMessage && serverMessage.includes("validation errors detected")) {
        return "Invalid input. Please check your entries and try again."
      }
      return serverMessage ?? "Invalid input. Please check your entries and try again."
    case 404:
      return serverMessage ?? "The requested resource was not found."
    case 503:
      // Transient — encourage retry
      return "Service temporarily unavailable. Please try again in a moment."
    case 500:
      // Don't expose internal details to users
      return "Something went wrong. Please try again or contact support if the issue persists."
    case 401:
      return "Session expired. Please sign in again."
    case 403:
      return "You don't have permission to perform this action."
    default:
      return serverMessage ?? `Unexpected error (${status}). Please try again.`
  }
}
